import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fmtMonto } from 'shared/format';
import { useAuth } from 'shared/context/AuthContext';
import { useDarkMode } from 'shared/context/DarkModeContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import API, { getImagenUrl } from 'shared/api';
import Spinner from 'shared/components/Spinner';
import {
  FiPlus, FiTrash2, FiEdit2, FiX, FiSearch, FiPackage, FiLayers,
  FiCheck, FiChevronDown, FiCheckSquare, FiUsers,
} from 'react-icons/fi';

const money = (n) => {
  const v = Number(n || 0);
  return v > 0 ? '$' + fmtMonto(v) : 'Gratis';
};

const Overlay = ({ onClose, children, COL, isMobile, maxWidth = 640 }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: COL.overlay, zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: isMobile ? 8 : 24, backdropFilter: 'blur(4px)',
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: COL.surface, borderRadius: 16, width: '100%', maxWidth,
        maxHeight: isMobile ? '96vh' : '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 24px 70px rgba(0,0,0,0.4)',
      }}
    >
      {children}
    </div>
  </div>
);

export default function ExtrasTab() {
  const { isAdmin, tienePermiso } = useAuth();
  const { COL } = useDarkMode();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const tp = (c) => tienePermiso(c);
  const puedeGestionar = isAdmin() || tp('inventario.extras');

  const [grupos, setGrupos] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [modalGrupo, setModalGrupo] = useState(null);
  const [grupoNombre, setGrupoNombre] = useState('');
  const [items, setItems] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [errorGrupo, setErrorGrupo] = useState('');

  const [modalProductos, setModalProductos] = useState(null); // grupo

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await API.get('/inventario/extras/grupos');
      setGrupos(r.data?.data || []);
    } catch (e) {
      console.error('Error cargando grupos de extras', e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setModalGrupo({});
    setGrupoNombre('');
    setItems([{ nombre: '', precio: '', activa_por_defecto: false }]);
    setErrorGrupo('');
  };

  const abrirEditar = (g) => {
    setModalGrupo({ editar: g });
    setGrupoNombre(g.nombre || '');
    setItems(
      (g.items || []).map((it) => ({
        nombre: it.nombre,
        precio: it.precio ? String(it.precio) : '',
        activa_por_defecto: !!it.activa_por_defecto,
      }))
    );
    setErrorGrupo('');
  };

  const cerrarGrupo = () => setModalGrupo(null);

  const setItem = (idx, campo, valor) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));
  };

  const addItem = () => setItems((prev) => [...prev, { nombre: '', precio: '', activa_por_defecto: false }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const guardarGrupo = async () => {
    const nombre = grupoNombre.trim();
    if (!nombre) { setErrorGrupo('El nombre del grupo es obligatorio'); return; }
    const itemsLimpios = items
      .map((it) => ({ ...it, nombre: (it.nombre || '').trim() }))
      .filter((it) => it.nombre);
    if (itemsLimpios.length === 0) { setErrorGrupo('Agrega al menos un extra'); return; }
    setGuardando(true);
    setErrorGrupo('');
    try {
      const payload = {
        nombre,
        items: itemsLimpios.map((it) => ({
          nombre: it.nombre,
          precio: parseFloat(it.precio) || 0,
          activa_por_defecto: !!it.activa_por_defecto,
        })),
      };
      if (modalGrupo.editar) {
        await API.put(`/inventario/extras/grupos/${modalGrupo.editar.id}`, payload);
      } else {
        await API.post('/inventario/extras/grupos', payload);
      }
      setModalGrupo(null);
      await cargar();
    } catch (e) {
      setErrorGrupo(e.response?.data?.detail || 'Error al guardar el grupo');
    } finally {
      setGuardando(false);
    }
  };

  const eliminarGrupo = async (g) => {
    if (!window.confirm(`Eliminar el grupo "${g.nombre}"? Se quitarán los extras de sus productos.`)) return;
    try {
      await API.delete(`/inventario/extras/grupos/${g.id}`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, flexWrap: 'wrap', gap: 8,
        }}
      >
        <div style={{ color: COL.muted, fontSize: 12, maxWidth: 640 }}>
          Crea secciones de extras (adicionales con precio) y asígnalas a los productos que quieras.
          Un producto puede estar en varias secciones y acumulará los extras de todas.
        </div>
        {puedeGestionar && (
          <button
            onClick={abrirNuevo}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px',
              borderRadius: 10, border: 'none', background: COL.pri2, color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <FiPlus size={14} /> Nueva sección
          </button>
        )}
      </div>

      {cargando ? (
        <Spinner />
      ) : grupos.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '56px 20px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, background: '#9334e612', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiLayers size={30} color="#9334e6" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: COL.text }}>Aún no hay secciones de extras</div>
          <div style={{ fontSize: 12, color: COL.muted, maxWidth: 420, lineHeight: 1.5 }}>
            Crea tu primera sección (ej. "Extras de hamburguesas") y asígnale los productos que quieras.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {grupos.map((g) => (
            <div
              key={g.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                borderRadius: 10, border: `1px solid ${COL.border}`, background: COL.surface,
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  width: 40, height: 40, borderRadius: 10, background: '#9334e618', color: '#9334e6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
              >
                <FiLayers size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: COL.text }}>{g.nombre}</div>
                <div style={{ fontSize: 12, color: COL.muted, marginTop: 2 }}>
                  {(g.items || []).map((it) => `${it.nombre}${it.precio > 0 ? ' (' + money(it.precio) + ')' : ''}`).join(' · ') || 'Sin extras'}
                </div>
              </div>
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                  borderRadius: 999, background: COL.blueBg, color: COL.pri2, fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}
              >
                <FiPackage size={13} /> {g.total_productos ?? 0} producto(s)
              </span>
              {puedeGestionar && (
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => setModalProductos(g)}
                    title="Gestionar productos"
                    style={{ ...btnIcon(COL), background: COL.greenBg, color: COL.green }}
                  >
                    <FiUsers size={15} /> Productos
                  </button>
                  <button onClick={() => abrirEditar(g)} title="Editar" style={{ ...btnIcon(COL), background: COL.blueBg, color: COL.pri2 }}>
                    <FiEdit2 size={15} />
                  </button>
                  <button onClick={() => eliminarGrupo(g)} title="Eliminar" style={{ ...btnIcon(COL), background: COL.redBg, color: COL.red }}>
                    <FiTrash2 size={15} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalGrupo && (
        <Overlay onClose={cerrarGrupo} COL={COL} isMobile={isMobile}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COL.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: COL.text }}>
              {modalGrupo.editar ? 'Editar sección' : 'Nueva sección de extras'}
            </div>
            <button onClick={cerrarGrupo} style={{ background: COL.bg, border: 'none', cursor: 'pointer', color: COL.muted, fontSize: 20, width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
          </div>
          <div style={{ padding: 16, overflowY: 'auto' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: COL.muted }}>Nombre de la sección</label>
            <input
              value={grupoNombre}
              onChange={(e) => { setGrupoNombre(e.target.value); setErrorGrupo(''); }}
              placeholder="Ej. Extras de hamburguesas"
              style={{ ...input(COL), marginTop: 6, marginBottom: 16 }}
            />
            <label style={{ fontSize: 12, fontWeight: 600, color: COL.muted }}>Extras</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
              {items.map((it, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={it.nombre}
                    onChange={(e) => setItem(idx, 'nombre', e.target.value)}
                    placeholder="Nombre del extra"
                    style={{ ...input(COL), flex: '1 1 140px', minWidth: 0 }}
                  />
                  <input
                    type="number" step="0.01" min="0"
                    value={it.precio}
                    onChange={(e) => setItem(idx, 'precio', e.target.value)}
                    placeholder="Precio"
                    style={{ ...input(COL), width: 90, flex: '0 0 auto' }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: COL.muted, whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={it.activa_por_defecto} onChange={(e) => setItem(idx, 'activa_por_defecto', e.target.checked)} />
                    Por defecto
                  </label>
                  <button onClick={() => removeItem(idx)} style={{ border: 'none', background: 'transparent', color: COL.red, cursor: 'pointer', padding: 6 }}>
                    <FiTrash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addItem}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '8px 12px',
                borderRadius: 8, border: `1px dashed ${COL.border}`, background: 'transparent',
                color: COL.pri2, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <FiPlus size={14} /> Agregar extra
            </button>
            {errorGrupo && <div style={{ fontSize: 12, color: COL.red, marginTop: 8 }}>{errorGrupo}</div>}
          </div>
          <div style={{ padding: '14px 20px', borderTop: `1px solid ${COL.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={cerrarGrupo} style={{ ...btnSecondary(COL) }}>Cancelar</button>
            <button onClick={guardarGrupo} disabled={guardando} style={{ ...btnPrimary(COL), opacity: guardando ? 0.6 : 1 }}>
              {guardando ? 'Guardando...' : 'Guardar sección'}
            </button>
          </div>
        </Overlay>
      )}

      {modalProductos && (
        <ProductosModal
          grupo={modalProductos}
          onClose={() => setModalProductos(null)}
          onChanged={cargar}
          COL={COL}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

function ProductosModal({ grupo, onClose, onChanged, COL, isMobile }) {
  const [asignados, setAsignados] = useState([]);
  const [cargandoAsignados, setCargandoAsignados] = useState(true);

  const [busqueda, setBusqueda] = useState('');
  const [q, setQ] = useState('');
  const [departamentos, setDepartamentos] = useState([]);
  const [departamento, setDepartamento] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState('');
  const searchTimer = useRef(null);
  const requestRef = useRef(0);

  const cargarAsignados = useCallback(async () => {
    setCargandoAsignados(true);
    try {
      const r = await API.get(`/inventario/extras/grupos/${grupo.id}/productos`);
      setAsignados(r.data?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setCargandoAsignados(false);
    }
  }, [grupo.id]);

  useEffect(() => {
    cargarAsignados();
    API.get('/inventario/departamentos?sede=todas')
      .then((r) => setDepartamentos(r.data?.data || []))
      .catch(() => {});
  }, [cargarAsignados]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setQ(busqueda.trim()), 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [busqueda]);

  const cargarCatalog = useCallback(async (reset, nextCursor) => {
    const id = ++requestRef.current;
    setCargando(true);
    try {
      const params = { sede: 'todas', limite: 60 };
      if (q) params.q = q;
      if (departamento) params.departamento = departamento;
      if (!reset && nextCursor) params.ultimo_cursor = nextCursor;
      const r = await API.get('/inventario/productos', { params });
      if (id !== requestRef.current) return;
      const data = r.data?.data || [];
      setCatalog((prev) => (reset ? data : [...prev, ...data]));
      setHasMore(r.data?.has_more || false);
      setCursor(r.data?.ultimo_cursor || null);
    } catch (e) {
      console.error(e);
    } finally {
      if (id === requestRef.current) setCargando(false);
    }
  }, [q, departamento]);

  useEffect(() => {
    cargarCatalog(true, null);
  }, [cargarCatalog]);

  const asignadosSet = new Set(asignados.map((s) => s.codigo));

  const asignar = async (codigos) => {
    setTrabajando(true);
    setError('');
    try {
      await API.post(`/inventario/extras/grupos/${grupo.id}/productos`, { codigos });
      await cargarAsignados();
      onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al asignar');
    } finally {
      setTrabajando(false);
    }
  };

  const desasignar = async (codigo) => {
    setTrabajando(true);
    setError('');
    try {
      await API.delete(`/inventario/extras/grupos/${grupo.id}/productos/${encodeURIComponent(codigo)}`);
      await cargarAsignados();
      onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al desasignar');
    } finally {
      setTrabajando(false);
    }
  };

  const toggle = (p) => {
    if (trabajando) return;
    if (asignadosSet.has(p.codigo)) desasignar(p.codigo);
    else asignar([p.codigo]);
  };

  const seleccionarPagina = () => {
    const nuevos = catalog.filter((p) => !asignadosSet.has(p.codigo)).map((p) => p.codigo);
    if (nuevos.length === 0) return;
    asignar(nuevos);
  };

  const filtroActivo = q || departamento;

  return (
    <Overlay onClose={onClose} COL={COL} isMobile={isMobile} maxWidth={1080}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COL.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: COL.text }}>{grupo.nombre}</div>
          <div style={{ fontSize: 12, color: COL.muted, marginTop: 2 }}>
            {(grupo.items || []).length} extras · toca un producto para agregarlo o quitarlo de esta sección (puede estar en varias)
          </div>
        </div>
        <button onClick={onClose} style={{ background: COL.bg, border: 'none', cursor: 'pointer', color: COL.muted, fontSize: 20, width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>&times;</button>
      </div>

      {/* Barra de búsqueda + filtros */}
      <div style={{ padding: '12px 20px', borderBottom: `1px solid ${COL.border}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', borderRadius: 10, border: `1px solid ${COL.border}`, background: COL.inputBg }}>
          <FiSearch size={16} color={COL.muted} />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre o código..."
            style={{ border: 'none', outline: 'none', background: 'transparent', padding: '10px 0', flex: 1, fontSize: 14, color: COL.text, fontFamily: 'inherit' }}
          />
          {busqueda && (
            <button onClick={() => setBusqueda('')} style={{ border: 'none', background: 'transparent', color: COL.muted, cursor: 'pointer', padding: 2 }}>
              <FiX size={14} />
            </button>
          )}
        </div>
        <div style={{ position: 'relative', minWidth: 200 }}>
          <select
            value={departamento}
            onChange={(e) => setDepartamento(e.target.value)}
            style={{ ...input(COL), width: '100%', appearance: 'none', paddingRight: 32, cursor: 'pointer' }}
          >
            <option value="">Todas las categorías</option>
            {departamentos.map((d) => (
              <option key={d.codigo} value={d.nombre}>{d.nombre}</option>
            ))}
          </select>
          <FiChevronDown size={15} color={COL.muted} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        </div>
        <button
          onClick={seleccionarPagina}
          disabled={trabajando}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderRadius: 10,
            border: `1px solid ${COL.border}`, background: COL.surface, color: COL.pri2,
            fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            opacity: trabajando ? 0.6 : 1,
          }}
        >
          <FiCheckSquare size={15} /> Seleccionar página
        </button>
      </div>

      {/* Asignados */}
      <div style={{ padding: '12px 20px 0', borderBottom: `1px solid ${COL.border}` }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COL.text, marginBottom: 8 }}>
          Productos en esta sección ({asignados.length})
        </div>
        {cargandoAsignados ? (
          <div style={{ padding: 8 }}><Spinner /></div>
        ) : asignados.length === 0 ? (
          <div style={{ fontSize: 12, color: COL.muted, padding: '0 0 12px' }}>
            Sin productos asignados. Usa la búsqueda o el catálogo de abajo para agregarlos.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 96, overflowY: 'auto', paddingBottom: 12 }}>
            {asignados.map((s) => (
              <span key={s.codigo} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px 4px 4px', borderRadius: 999, background: COL.blueBg, color: COL.pri2, fontSize: 12, fontWeight: 600 }}>
                {getImagenUrl(s.thumb_url || s.imagen_url) ? (
                  <img src={getImagenUrl(s.thumb_url || s.imagen_url)} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <FiPackage size={14} />
                )}
                <span style={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.descripcion}</span>
                <button onClick={() => desasignar(s.codigo)} disabled={trabajando} style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0, display: 'flex' }}>
                  <FiX size={13} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Catálogo */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {cargando && catalog.length === 0 ? (
          <div style={{ padding: 24 }}><Spinner /></div>
        ) : catalog.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: COL.muted }}>
            <FiPackage size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {filtroActivo ? 'Sin resultados' : 'No hay productos'}
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '150px'}, 1fr))`, gap: 10 }}>
              {catalog.map((p) => {
                const sel = asignadosSet.has(p.codigo);
                const img = getImagenUrl(p.thumb_url || p.imagen_url);
                return (
                  <button
                    key={p.codigo}
                    onClick={() => toggle(p)}
                    disabled={trabajando}
                    style={{
                      display: 'flex', flexDirection: 'column', position: 'relative',
                      borderRadius: 12, overflow: 'hidden', border: `2px solid ${sel ? COL.green : COL.border}`,
                      background: COL.surface, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      boxShadow: sel ? `0 0 0 3px ${COL.greenBg}` : 'none', transition: 'all 0.12s',
                      opacity: trabajando ? 0.7 : 1,
                    }}
                  >
                    <div style={{ width: '100%', height: 96, background: COL.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                      {img ? (
                        <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <FiPackage size={26} color={COL.faint} />
                      )}
                      {sel && (
                        <div style={{ position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: '50%', background: COL.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FiCheck size={13} />
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COL.text, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 30 }}>
                        {p.descripcion || p.codigo}
                      </div>
                      <div style={{ fontSize: 10, color: COL.muted }}>{p.codigo}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 4px' }}>
                <button
                  onClick={() => cargarCatalog(false, cursor)}
                  disabled={cargando}
                  style={{ ...btnSecondary(COL), opacity: cargando ? 0.6 : 1 }}
                >
                  {cargando ? 'Cargando...' : 'Cargar más productos'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {error && (
        <div style={{ padding: '8px 20px', borderTop: `1px solid ${COL.border}`, fontSize: 12, color: COL.amber, background: COL.amberBg }}>{error}</div>
      )}

      <div style={{ borderTop: `1px solid ${COL.border}`, padding: '12px 20px', background: COL.surface, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
          {asignados.length} producto(s) en esta sección
        </div>
        <button onClick={onClose} style={{ ...btnPrimary(COL) }}>Listo</button>
      </div>
    </Overlay>
  );
}

const btnIcon = (COL) => ({
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8,
  border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
});

const input = (COL) => ({
  padding: '9px 12px', borderRadius: 10, border: `1px solid ${COL.border}`,
  background: COL.inputBg, color: COL.text, fontSize: 13, fontFamily: 'inherit', outline: 'none',
});

const btnPrimary = (COL) => ({
  padding: '10px 18px', borderRadius: 10, border: 'none', background: COL.pri2, color: '#fff',
  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
});

const btnSecondary = (COL) => ({
  padding: '10px 18px', borderRadius: 10, border: `1px solid ${COL.border}`,
  background: COL.surface, color: COL.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
});
